/* @flow */
import * as React from "react";
import Raven from "raven-js";
import { withRouter } from "react-router-dom";
import enqueueIn from "nx-components/utils/enqueueIn";
import PageTransition from "nexia_mobile/components/Routing/PageTransition";
import type { MemoryLocation } from "nexia_mobile/ReactRouterTypes";
import type { NexiaHistory } from "nexia_mobile/history";
import SubMemoryRouter from "nexia_mobile/components/Routing/SubMemoryRouter";

function buildScreenGuard(): HTMLElement {
  const screenGuard = document.createElement("div");
  screenGuard.className = "screen-guard";
  Object.assign(screenGuard.style, {
    position: "fixed",
    top: "0",
    right: "0",
    bottom: "0",
    left: "0",
    zIndex: "9001", // over 9000!!
  });
  return screenGuard;
}

type Props = {
  history: NexiaHistory,
  children?: React.Node,
};

let transitionPromise = Promise.resolve();
export function getTransitionPromise(): Promise<void> {
  return new Promise((resolve) => {
    // Always delay at least a task first so that if:
    // * A click event or etc causes navigation
    // * and we want to set up .then callbacks within that click event handler
    //   for when transitions are done
    // * then the componentDidUpdate in HistoryPageTransitionController can
    //   update transitionPromise to the new instance which refers to the
    //   current transition,
    // * before those .then callbacks get called.
    // Otherwise, those click event handler .then callbacks would fire
    // immediately, because transitionPromise would be a Promise that got
    // resolved a long time ago.
    //
    // We use enqueueIn here instead of setTimeout because device parsing awaits
    // getTransitionPromise, and we need device parsing to not be throttled.
    enqueueIn(resolve, 0);
  })
    .then(() => transitionPromise)
    .catch((err) => {
      // We want to report this, because it shouldn't happen, but in case it
      // does, we want this promise to still resolve so that consumers of
      // getTransitionPromise don't need to guard this promise defensively.
      Raven.captureException(err);
    });
}

class HistoryPageTransitionController extends React.Component<Props> {
  _previousHistoryKey: string;
  _pageTransitions: WeakMap<MemoryLocation, PageTransition> = new WeakMap();
  _visibleLocations: Set<MemoryLocation> = new Set();
  _isMounted: boolean;
  _screenGuard: ?HTMLElement;

  UNSAFE_componentWillMount() {
    const { history } = this.props;
    this._visibleLocations.add(history.entries[history.index]);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _forceUpdateIfMounted() {
    if (this._isMounted) {
      this.forceUpdate();
    }
  }

  // The user should not be able to interact with stuff while the page is
  // transitioning, because if they can, they can eg. tap a tile 10 times
  // which calls history.push("/app/device/details") 10 times in a row.
  // This renders a div on top of the page, which prevents the user and also the
  // BackButtonResponder in BackButton from interacting with anything.
  _guardScreenDuringTransition(shouldGuard: boolean) {
    const { body } = document;
    if (body == null) {
      return;
    }
    if (shouldGuard) {
      if (this._screenGuard == null) {
        this._screenGuard = buildScreenGuard();
      }
      body.appendChild(this._screenGuard);
    } else if (this._screenGuard && body.contains(this._screenGuard)) {
      body.removeChild(this._screenGuard);
    }
  }

  _getHistoryKey = () => {
    const { history } = this.props;
    return [
      history.index,
      history.entries.map((location) => location.key).join(","),
      history.action,
    ].join("_");
  };

  _slideNewOntoOld(newLocation: MemoryLocation, oldLocation: MemoryLocation) {
    this._guardScreenDuringTransition(true);

    this._visibleLocations.add(newLocation);
    if (oldLocation != null) {
      this._visibleLocations.add(oldLocation);
    }
    this.forceUpdate();

    const newPageTransition = this._pageTransitions.get(newLocation);

    const actions = [];
    if (newPageTransition != null) {
      actions.push(newPageTransition.slideIn());
    }

    transitionPromise = transitionPromise.then(() =>
      Promise.all(actions).then(() => {
        this._visibleLocations.add(newLocation);
        this._visibleLocations.delete(oldLocation);
        this._forceUpdateIfMounted();
        this._guardScreenDuringTransition(false);
        if (newPageTransition != null) {
          newPageTransition.focus();
        }
      })
    );
  }

  _slideOldsOffOfNew(
    oldLocations: Array<MemoryLocation>,
    newLocation: MemoryLocation
  ) {
    this._guardScreenDuringTransition(true);

    const actions = [];
    this._visibleLocations.add(newLocation);
    const newPageTransition = this._pageTransitions.get(newLocation);
    if (newPageTransition != null) {
      actions.push(newPageTransition.teleportIn());
    }

    oldLocations.forEach((oldLocation) => {
      const oldPageTransition = this._pageTransitions.get(oldLocation);
      if (oldPageTransition != null) {
        actions.push(oldPageTransition.slideOut());
      }
    });

    transitionPromise = transitionPromise.then(() =>
      Promise.all(actions).then(() => {
        this._visibleLocations.add(newLocation);
        oldLocations.forEach((oldLocation) => {
          this._visibleLocations.delete(oldLocation);
        });
        this._forceUpdateIfMounted();
        this._guardScreenDuringTransition(false);
        if (newPageTransition != null) {
          newPageTransition.focus();
        }
      })
    );

    this.forceUpdate();
  }

  componentDidUpdate() {
    // We need to react to route changes in componentDidUpdate instead of
    // UNSAFE_componentWillReceiveProps because we need our refs to be present before
    // we can call the relevant ref methods.
    const { history } = this.props;

    // There is a possibility that this update was *not* caused by a route
    // change; if that's the case, don't animate anything.
    const historyKey = this._getHistoryKey();
    if (historyKey === this._previousHistoryKey) {
      return;
    }
    this._previousHistoryKey = historyKey;

    const action = history.computeEffectiveAction();

    if (action === "PUSH") {
      // User is navigating forwards. Slide the page they are navigating
      // onto on top of the page they just came from.
      const newLocation = history.entries[history.index];
      const oldLocation = history.entries[history.index - 1];

      this._slideNewOntoOld(newLocation, oldLocation);
    } else if (action === "POP") {
      // User is navigating back. Slide the page they just came from off the top
      // of the page they are navigating onto.
      const oldLocations = history.entries.slice(history.index + 1);
      const newLocation = history.entries[history.index];

      this._slideOldsOffOfNew(oldLocations, newLocation);
    } else if (action === "REPLACE") {
      // The route got replaced. Usually this happens programmatically.
      // Transition it as if it were a PUSH.
      const newLocation = history.entries[history.index];
      const oldLocation =
        history.replacedLocation && history.replacedLocation.location;

      if (oldLocation != null) {
        this._slideNewOntoOld(newLocation, oldLocation);
      }
    } else if (action === "INITIAL_REPLACE") {
      // This is just the /app replace that happens when the app launches.
      // Don't transition the the page in; show it now.
      const newLocation = history.entries[history.index];
      const oldLocation =
        history.replacedLocation && history.replacedLocation.location;

      if (oldLocation != null) {
        this._visibleLocations.delete(oldLocation);
      }
      this._visibleLocations.add(newLocation);

      const newPageTransition = this._pageTransitions.get(newLocation);
      if (newPageTransition != null) {
        newPageTransition.teleportIn();
      }
      this.forceUpdate();
    } else if (action === "CLEAR_HISTORY_AND_PUSH") {
      // A clearHistoryAndPush occurred. Stop rendering all the locations that
      // got POPped and slide the new location in on top of the old one.

      const newLocation = history.entries[0];
      const poppedLocations = history.entries.slice(1);
      const oldLocation =
        history.replacedLocation && history.replacedLocation.location;

      poppedLocations.forEach((location) => {
        this._visibleLocations.delete(location);
      });

      if (oldLocation != null) {
        this._slideNewOntoOld(newLocation, oldLocation);
      }
    } else if (action === "VIRTUAL_POP") {
      // A virtual pop occurs from a clearHistoryAndVirtualPopTo, so we need to
      // treat this just like a clearHistoryAndPush, but instead of sliding the
      // new view onto the old view, we slide the old view off of the new view.

      const newLocation = history.entries[0];
      const poppedLocations = history.entries.slice(1);
      const replacedLocation =
        history.replacedLocation && history.replacedLocation.location;

      let oldLocations;
      if (replacedLocation != null) {
        oldLocations = poppedLocations.concat(replacedLocation);
      } else {
        oldLocations = poppedLocations;
      }

      this._slideOldsOffOfNew(oldLocations, newLocation);
    }
  }

  render() {
    const { children, history } = this.props;
    const action = history.computeEffectiveAction();

    const entries = history.entries;
    let entriesWithReplacedLocation = entries;
    if (
      history.replacedLocation != null &&
      this._visibleLocations.has(history.replacedLocation.location)
    ) {
      const { previousIndex, location } = history.replacedLocation;
      // We need to render the replaced location so that it's visible while we
      // slide the new location on top of it. So add it to entries.
      entriesWithReplacedLocation = [...entries];
      entriesWithReplacedLocation.splice(previousIndex - 1, 0, location);
    }

    return (
      <React.Fragment>
        {entriesWithReplacedLocation.map((location, index) => {
          const visible = this._visibleLocations.has(location);
          let zIndex = index;
          if (
            action === "CLEAR_HISTORY_AND_PUSH" &&
            history.action === "REPLACE" &&
            location === history.entries[0] // The new location
          ) {
            // The new location needs to be on top of everything else.
            zIndex = entriesWithReplacedLocation.length + 1;
          }

          return (
            <PageTransition
              id={location.pathname}
              key={location.key}
              ref={(inst) => {
                if (inst != null) {
                  this._pageTransitions.set(location, inst);
                }
              }}
              style={{
                zIndex: visible ? zIndex : "",
                display: visible ? "" : "none",
              }}
              initialInValue={visible}
            >
              {visible ? (
                <SubMemoryRouter
                  entries={entriesWithReplacedLocation}
                  index={index}
                >
                  <IgnoreUpdateWhenLeaving
                    isLeaving={entries[history.index] !== location}
                  >
                    {children}
                  </IgnoreUpdateWhenLeaving>
                </SubMemoryRouter>
              ) : null}
            </PageTransition>
          );
        })}
      </React.Fragment>
    );
  }
}

export default withRouter(HistoryPageTransitionController);

type IgnoreUpdateWhenLeavingProps = {
  isLeaving: boolean,
  children: React.Node,
};

// We don't care about updating the render tree for a Page which is leaving
// (and doing so is expensive for no benefit) so short-circuit renders at that
// level with shouldComponentUpdate.
class IgnoreUpdateWhenLeaving extends React.Component<IgnoreUpdateWhenLeavingProps> {
  shouldComponentUpdate(nextProps: IgnoreUpdateWhenLeavingProps) {
    return !nextProps.isLeaving;
  }

  render() {
    return this.props.children;
  }
}



// WEBPACK FOOTER //
// ./src/components/Routing/HistoryPageTransitionController.jsx